import { useAuth } from '@contexts/auth';
import {
  useAccountSwitcher,
  type SwitcherAccount,
} from '@features/account-view/useAccountSwitcher';

/** The part of a signed-in user's organization the logo is read from. */
type OrgWithLogo = { _id: string; logo?: { url?: string } };

const sameName = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The icon the Division cell shows beside a division's name, from what the
 * sidebar switcher draws: the Postgres division logo, then its legacy
 * organization's logo (the ids are shared), else, for the
 * division that carries its account's name, the account's logo. Null: the
 * name alone.
 */
export function divisionLogoOf(
  division: { id?: string | null; name?: string | null },
  orgs: readonly OrgWithLogo[],
  accounts: readonly SwitcherAccount[],
): string | null {
  const { id, name } = division;
  if (!id) return null;
  const account = accounts.find(a => a.divisionIds.includes(id));
  const pg = account?.divisions?.find(d => d.id === id)?.logoUrl;
  if (pg) return pg;
  const own = orgs.find(org => org._id === id)?.logo?.url;
  if (own) return own;
  if (account?.logoUrl && name && sameName(name, account.name)) {
    return account.logoUrl;
  }
  return null;
}

/** `divisionLogoOf` over the signed-in user's organizations and accounts. */
export function useDivisionLogos() {
  const { user } = useAuth();
  const accounts = useAccountSwitcher();
  const orgs = (user?.organizations ?? []) as OrgWithLogo[];
  return (division: { id?: string | null; name?: string | null }) =>
    divisionLogoOf(division, orgs, accounts);
}
